import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { logger } from '@/utils/logger';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import useRegionStore from '@/context/RegionStore';
import useAvailableRegionsStore from '@/context/AvailableRegionsStore';
import useCountryDetailsStore from '@/context/CountryDetailsStore';
import useOrderSummaryStore from '@/context/OrderSummaryStore';
import { fetchViewerCountry } from '@/services/api';
import { isSpecialLink } from '@/utils/specialLinks';
import { changeRegionPreservePath } from '@/utils/regionDetection';
import { generateProductUrl, parseVariantFromUrl, cleanUrlParams } from '@/utils/productUrls';
import ProductErrorBoundary from './ProductErrorBoundary';
import CheckoutErrorBoundary from './CheckoutErrorBoundary';
import FunLoading from './FunLoading';
import Home from '@/pages/Home';
import AbandonedUnsubscribe from '@/pages/AbandonedUnsubscribe';
import ProductPage from '@/pages/ProductPage';
import ComingSoon from '@/pages/ComingSoon';
import NotFound from '@/pages/NotFound';

// Lazy load checkout page - users rarely visit checkout directly, loaded on demand
const CheckoutPage = lazy(() => import('@/pages/checkout'));
const RepairPartsIndex = lazy(() => import('@/pages/repair/RepairPartsIndex'));
const RepairPartDetail = lazy(() => import('@/pages/repair/RepairPartDetail'));

interface RegionWrapperProps {
    toggleOverlay: (state: boolean) => void;
    navHeight: number;
    setNavVisibilityOverride: (state: boolean | null) => void;
}

const RegionWrapper: React.FC<RegionWrapperProps> = ({ toggleOverlay, navHeight, setNavVisibilityOverride }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { storeRegion, chosenRegion, detectedRegion, isDetectingRegion, showRegionModal, acceptedRegion, setStoreRegion, setDetectedRegion, setChosenRegion, setShowRegionModal, setIsDetectingRegion, setAcceptedRegion } = useRegionStore();
    const { isRegionAvailable, availableRegions } = useAvailableRegionsStore();
    const { countries, error: countryDetailsError } = useCountryDetailsStore();
    const { currentOrder } = useOrderSummaryStore();

    // Track which region mismatch scenario we've already shown the modal for
    const shownModalForScenarioRef = useRef<string | null>(null);

    // Restore region from last checkout if user revisits the site
    useEffect(() => {
        if (currentOrder?.region && !location.pathname.includes(currentOrder.region)) {
            logger.info('component', 'RegionWrapper: Restoring region from last order', { region: currentOrder.region, currentPath: location.pathname });
            setChosenRegion(currentOrder.region);
        }
    }, []);

    const detectRegion = async () => {
        logger.debug('component', 'RegionWrapper: Starting region detection');
        setIsDetectingRegion(true);
        const detectedCountryCode = await fetchViewerCountry();
        logger.info('component', `RegionWrapper: Country detected as ${detectedCountryCode}`);
        setDetectedRegion(detectedCountryCode);
        setIsDetectingRegion(false);
    };

    // Initialize: detect viewer's country and set up store region
    // Also extract region from URL if present (e.g., /us/products -> us)
    useEffect(() => {
        logger.info('component', 'RegionWrapper: Initializing region detection', {
            pathname: location.pathname
        });

        // Extract region from URL path if present
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const regionPrefix = pathSegments[0];

        logger.info('component', 'RegionWrapper: URL analysis', {
            pathSegments,
            regionPrefix,
            isValidRegionCode: regionPrefix && /^[a-z]{2}$/i.test(regionPrefix)
        });

        // Check if first path segment is a valid region code (2 letters)
        // API uses lowercase codes (us, gb, fr, etc.)
        if (regionPrefix && /^[a-z]{2}$/i.test(regionPrefix)) {
            const regionCode = regionPrefix.toLowerCase();
            logger.info('component', 'RegionWrapper: Setting region from URL', { regionCode, storeRegion });
            setStoreRegion(regionCode);
            setChosenRegion(regionCode);
        } else {
            // Default to GB for root path
            logger.info('component', 'RegionWrapper: Using default GB region', { storeRegion });
            setChosenRegion(storeRegion);
        }

        // Start detecting the user's actual region in the background
        detectRegion();
    }, [location.pathname]);

    // Handle region changes by updating the URL to match
    useEffect(() => {
        if (chosenRegion && chosenRegion !== storeRegion) {
            logger.info('component', 'RegionWrapper: User selected different region, updating URL', {
                from: storeRegion,
                to: chosenRegion,
                currentPath: location.pathname
            });
            const newPath = changeRegionPreservePath(location.pathname, chosenRegion, location.search);
            navigate(newPath);
            setStoreRegion(chosenRegion);
        }
    }, [chosenRegion]);

    // Initialize available regions when countries data is loaded
    useEffect(() => {
        if (countries) {
            useAvailableRegionsStore.getState().setAvailableRegions(countries);
            logger.info('component', 'RegionWrapper: Available regions initialized', {
                regionCount: Object.keys(countries).length,
            });
        }
    }, [countries]);

    // Handle region mismatch detection
    // Show modal if detected region differs from store region and is available
    useEffect(() => {
        if (!detectedRegion || !countries) {
            logger.debug('component', 'RegionWrapper: Waiting for data', {
                hasDetectedRegion: !!detectedRegion,
                hasCountries: !!countries,
            });
            return; // Wait until detection is complete
        }

        logger.info('component', 'RegionWrapper: Checking region mismatch', {
            storeRegion,
            detectedRegion,
            chosenRegion,
            countryKeys: Object.keys(countries),
            timestamp: new Date().toISOString()
        });

        // Check if both store region and detected region are available
        const isStoreRegionAvailable = Object.entries(countries).some(([key, value]) => {
            if (key.toLowerCase() === storeRegion.toLowerCase()) return true;
            if ('type' in value && value.type === 'group') {
                return storeRegion.toLowerCase() in value.countries;
            }
            return false;
        });

        const isDetectedRegionAvailable = Object.entries(countries).some(([key, value]) => {
            if (key.toLowerCase() === detectedRegion.toLowerCase()) return true;
            if ('type' in value && value.type === 'group') {
                return detectedRegion.toLowerCase() in value.countries;
            }
            return false;
        });

        // Scenario: Detected region differs from store region OR store region is not available
        if (detectedRegion !== storeRegion || !isStoreRegionAvailable) {
            // Create a key that identifies this specific region mismatch scenario
            const scenarioKey = `${storeRegion}-${detectedRegion}`;

            // Only attempt to show modal if:
            // 1. Modal is not already visible
            // 2. We haven't already shown this scenario AND closed it
            if (!showRegionModal && shownModalForScenarioRef.current !== scenarioKey) {
                if (isDetectedRegionAvailable && isStoreRegionAvailable) {
                    // Both regions are available - show "Visiting [store]?" modal
                    logger.info('component', 'RegionWrapper: Showing region mismatch modal (both available)', {
                        storeRegion,
                        detectedRegion,
                    });
                    shownModalForScenarioRef.current = scenarioKey;
                    setShowRegionModal(true);
                } else if (!isStoreRegionAvailable) {
                    // Store region not available - show "Region Unavailable" modal with list
                    logger.info('component', 'RegionWrapper: Showing region unavailable modal (store not available)', {
                        storeRegion,
                        detectedRegion,
                    });
                    shownModalForScenarioRef.current = scenarioKey;
                    setShowRegionModal(true);
                } else if (!isDetectedRegionAvailable) {
                    // Detected region not available - show "Region Unavailable" modal with list
                    logger.info('component', 'RegionWrapper: Showing region unavailable modal (detected not available)', {
                        detectedRegion,
                    });
                    shownModalForScenarioRef.current = scenarioKey;
                    setShowRegionModal(true);
                }
            }
        } else {
            // No mismatch - close modal and reset scenario ref
            if (showRegionModal) {
                setShowRegionModal(false);
            }
            shownModalForScenarioRef.current = null;
            // Keep acceptedRegion as is - don't reset it, user's choice should persist
        }
    }, [detectedRegion, storeRegion, countries, acceptedRegion, showRegionModal]);

    const redirectRegion = (chosenRegion || storeRegion || detectedRegion || 'gb').toLowerCase();
    const redirectToRegionPath = (path = location.pathname) => (
        <Navigate to={changeRegionPreservePath(path, redirectRegion, location.search)} replace />
    );

    return (
        <Routes>
            {/* Root paths (no region prefix, defaults to GB) */}
            <Route path="/" element={<Home />} />
            <Route path="products/wired-headphones-1" element={redirectToRegionPath()} />
            <Route path="products/wired-headphones-1/:variant" element={redirectToRegionPath()} />
            <Route path="shop/checkout/*" element={redirectToRegionPath()} />
            <Route path="repair/parts" element={redirectToRegionPath()} />
            <Route path="repair/parts/:partId" element={redirectToRegionPath()} />
            <Route path="emails/abandoned/unsubscribe" element={<AbandonedUnsubscribe />} />
            <Route path="comingsoon" element={redirectToRegionPath()} />

            {/* Region-prefixed paths (e.g., /us/, /de/, /gb/) */}
            <Route path=":region/" element={<Home />} />
            <Route path=":region/products/wired-headphones-1" element={
                <ProductErrorBoundary>
                    <ProductPage
                        toggleOverlay={toggleOverlay}
                        navHeight={navHeight}
                        setNavVisibilityOverride={setNavVisibilityOverride}
                    />
                </ProductErrorBoundary>
            } />
            <Route path=":region/products/wired-headphones-1/:variant" element={
                <ProductErrorBoundary>
                    <ProductPage
                        toggleOverlay={toggleOverlay}
                        navHeight={navHeight}
                        setNavVisibilityOverride={setNavVisibilityOverride}
                    />
                </ProductErrorBoundary>
            } />
            <Route path=":region/shop/checkout/*" element={
                <CheckoutErrorBoundary>
                    <Suspense fallback={<FunLoading />}>
                        <CheckoutPage />
                    </Suspense>
                </CheckoutErrorBoundary>
            } />
            <Route path=":region/repair/parts" element={
                <Suspense fallback={<FunLoading />}>
                    <RepairPartsIndex />
                </Suspense>
            } />
            <Route path=":region/repair/parts/:partId" element={
                <Suspense fallback={<FunLoading />}>
                    <RepairPartDetail />
                </Suspense>
            } />
            <Route path=":region/emails/abandoned/unsubscribe" element={<AbandonedUnsubscribe />} />
            <Route path=":region/comingsoon" element={<ComingSoon />} />

            {/* Catch-all for not found */}
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

export default RegionWrapper; 
